import { useMemo, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useSelector, useDispatch } from 'react-redux';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import DateUtils from 'utils/DateUtils';
import FormCheckbox from 'core/forms/FormCheckbox';
import useCopyTextToClipboard from 'hooks/useCopyTextToClipboard';
import TableContainer from 'core/table/TableContainer';
import TanstackReactTable from 'core/table/TanstackReactTable';

import constants from 'app/config/constants';
import Dimensions from 'app/config/Dimensions';
import { getPartnerApiLogs, getPartnerApiLog } from 'app/features/partner-api-logs/partnerApiLogSaga';
import { openPartnerApiLogDetails } from 'app/features/partner-api-logs/partnerApiLogSlice';
import PartnerApiLogsActions from './PartnerApiLogsActions';
import PartnerApiLogPagination from './PartnerApiLogPagination';

function EndpointCell({ value }) {
  const copyText = useCopyTextToClipboard();

  return (
    <Box display="flex" alignItems="center" gap={0.5} maxWidth={280}>
      <Tooltip title={value} placement="top" arrow>
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {value}
        </Box>
      </Tooltip>
      <Tooltip title="Copy" placement="top" arrow>
        <IconButton
          size="small"
          aria-label="Copy endpoint"
          onClick={(event) => {
            event.stopPropagation();
            copyText(value);
          }}
        >
          <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function PartnerApiLogs() {
  const dispatch = useDispatch();
  const [columnVisibility, setColumnVisibility] = useState({});

  const logsState = useSelector((state) => state.partnerApiLogs.index);
  const { data, params, isLoading } = logsState;

  const columns = useMemo(
    () => [
      {
        id: constants.TABLE_CHECKBOX_SELECT_CHECKBOX_ID,
        header: ({ table }) => (
          <FormCheckbox
            forTableHead={true}
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div>
            <FormCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: constants.TABLE_SERIAL_NUMBER_COLUMN_ID,
        header: 'SN',
        accessorFn: (row) => row.sn,
      },
      {
        header: 'Request ID',
        accessorFn: (row) => (row.requestId ? row.requestId : 'N/A'),
      },
      {
        header: 'Partner Code',
        accessorFn: (row) => (row.partnerCode ? row.partnerCode : 'N/A'),
      },
      {
        header: 'Method',
        accessorFn: (row) => (row.method ? row.method : 'N/A'),
      },
      {
        header: 'Endpoint',
        accessorFn: (row) => (row.endpoint ? row.endpoint : 'N/A'),
        cell: ({ getValue }) => <EndpointCell value={getValue()} />,
      },
      {
        header: 'Created At',
        accessorFn: (row) => (row.createdTs ? DateUtils.isoToString(row.createdTs) : 'N/A'),
      },
      {
        header: 'Updated At',
        accessorFn: (row) => (row.updatedTs ? DateUtils.isoToString(row.updatedTs) : 'N/A'),
      },
      {
        id: constants.TABLE_ACTION_COLUMN_ID,
        header: 'Action',
        accessorFn: (row) => row.id,
        cell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton
              size={Dimensions.table.iconButton.size}
              onClick={() => {
                const id = row.original?.id;
                if (id) dispatch(getPartnerApiLog(id));
                else dispatch(openPartnerApiLogDetails(row.original));
              }}
            >
              <AspectRatioIcon fontSize={Dimensions.table.iconButton.iconSize} />
            </IconButton>
          </Box>
        ),
      },
    ],
    [dispatch],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    dispatch(getPartnerApiLogs({ ...params }));
  }, [JSON.stringify(params)]);

  return (
    <TableContainer>
      <PartnerApiLogsActions table={table} />
      <TanstackReactTable
        table={table}
        isLoading={isLoading}
        data={data}
        columnsCount={
          columns.length - Object.entries(columnVisibility ?? {}).filter(([, v]) => !v).length
        }
      />
      <PartnerApiLogPagination />
    </TableContainer>
  );
}
